import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const VerifyEmail = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Guard to prevent duplicate API calls (React 18 StrictMode runs effects twice)
  const verificationAttempted = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent duplicate calls
      if (verificationAttempted.current) {
        return;
      }
      verificationAttempted.current = true;

      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          setError('Verification token is missing');
          setLoading(false);
          return;
        }

        // Use a vanilla axios instance instead of the configured one to avoid authentication issues
        // The configured axios instance tries to use tokens from localStorage which don't exist yet
        const apiUrl = process.env.REACT_APP_API_URL || ''; // Default to empty string if undefined
        const response = await axios.post(`${apiUrl}/api/v1/auth/verify-email`, { token });

        if (response.status === 200) {
          setSuccess(true);

          // Extract the registration token from the response
          const { registration_token, registration_url } = response.data;

          // After successful verification, redirect to completion page with registration token
          setTimeout(() => {
            if (registration_url) {
              // If a full URL is provided, redirect to it
              window.location.href = registration_url;
            } else {
              // Otherwise, navigate to the registration completion page with the token
              navigate(`/complete-registration?token=${registration_token}`);
            }
          }, 3000);
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError(
          err.response?.data?.detail ||
            'Email verification failed. Please try again or contact support.'
        );
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src={process.env.PUBLIC_URL + '/maincar.png'}
            alt="InternHub Logo"
            className="h-14 mb-2"
          />
          <span className="text-blue-600 text-3xl font-bold ml-2 self-center">InternHub</span>
        </div>
        <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
          Email Verification
        </h2>
        <div className="mt-2 text-center text-sm text-gray-600">
          {loading ? (
            <p className="font-medium">Verifying your email address...</p>
          ) : success ? (
            <p className="font-medium text-green-600">
              Your email has been successfully verified! You'll be redirected to complete your
              registration and set your password...
            </p>
          ) : (
            <p className="font-medium">Verification Status</p>
          )}
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
        <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : success ? (
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <svg
                  className="h-12 w-12 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              <div className="text-center">
                <Link
                  to="/register"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Return to registration
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
